<template>
  <div 
    v-if="shouldShowStatus" 
    class="flex items-center space-x-1 transition-all duration-300"
    :class="statusClasses"
  >
    <!-- Icono de estado -->
    <div class="relative">
      <!-- Sending state (spinning) -->
      <svg 
        v-if="status === 'sending'" 
        class="w-3 h-3 text-slate-400 animate-spin" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
      
      <!-- Delivered state (single check) -->
      <svg 
        v-else-if="status === 'delivered'" 
        class="w-3 h-3 text-slate-500 transition-colors duration-200" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
      </svg>
      
      <!-- Read state (double check) -->
      <svg 
        v-else-if="status === 'read'" 
        class="w-3 h-3 text-blue-500 transition-colors duration-200" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      
      <!-- Error state (warning triangle) -->
      <svg 
        v-else-if="status === 'error'" 
        class="w-3 h-3 text-red-500 transition-colors duration-200" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      
      <!-- Failed state (X) -->
      <svg 
        v-else-if="status === 'failed'" 
        class="w-3 h-3 text-red-500 transition-colors duration-200" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
    
    <!-- Texto de estado (opcional, solo para estados especiales) -->
    <span 
      v-if="showStatusText" 
      class="text-xs transition-all duration-200"
      :class="statusTextClasses"
    >
      {{ statusText }}
    </span>
    
    <!-- Timestamp (opcional) -->
    <span 
      v-if="showTimestamp && timestamp" 
      class="text-xs text-slate-400 transition-all duration-200"
    >
      {{ formattedTimestamp }}
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  status: {
    type: String,
    required: true,
    validator: (value) => ['sending', 'delivered', 'read', 'error', 'failed'].includes(value)
  },
  timestamp: {
    type: [String, Date, Number],
    default: null
  },
  showTimestamp: {
    type: Boolean,
    default: false
  },
  showStatusText: {
    type: Boolean,
    default: false
  },
  isUserMessage: {
    type: Boolean,
    default: false
  }
});

// Solo mostrar estados para mensajes del usuario
const shouldShowStatus = computed(() => {
  return props.isUserMessage && props.status && props.status !== 'none';
});

const statusClasses = computed(() => {
  const baseClasses = 'opacity-0 group-hover:opacity-100';
  
  switch (props.status) {
    case 'sending':
      return `${baseClasses} opacity-70`;
    case 'delivered':
      return `${baseClasses} opacity-60`;
    case 'read':
      return `${baseClasses} opacity-80`;
    case 'error':
    case 'failed':
      return `${baseClasses} opacity-100`;
    default:
      return baseClasses;
  }
});

const statusTextClasses = computed(() => {
  switch (props.status) {
    case 'sending':
      return 'text-slate-500';
    case 'delivered':
      return 'text-slate-600';
    case 'read':
      return 'text-blue-600';
    case 'error':
    case 'failed':
      return 'text-red-600';
    default:
      return 'text-slate-500';
  }
});

const statusText = computed(() => {
  if (!props.showStatusText) return '';
  
  switch (props.status) {
    case 'sending':
      return 'Enviando...';
    case 'delivered':
      return 'Entregado';
    case 'read':
      return 'Leído';
    case 'error':
      return 'Error';
    case 'failed':
      return 'Falló';
    default:
      return '';
  }
});

const formattedTimestamp = computed(() => {
  if (!props.timestamp) return '';
  
  const date = new Date(props.timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  
  if (diffInMinutes < 1) {
    return 'Ahora';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  } else if (diffInMinutes < 1440) { // 24 hours
    return `${Math.floor(diffInMinutes / 60)}h`;
  } else {
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
});
</script>
